import rsaRaw from '../../public/data/rsa.json'
import type { AmbitoId } from './ambitiStats'

type RawRsa = {
  Info: string
  'Nome struttura': string
  'Località': string
  'Indirizzo': string
  'Provincia': string
  'Nucleo Alzheimer': string
  'Retta Min.': string
  'Retta Max.': string
  'Retta Min. Alzheimer': string
  'Retta Max. Alzheimer': string
  'Lista attesa ordinaria': number
  'Lista attesa Alzheimer': number
  'Data aggiornamento': string
  cudes?: string
  telefono?: string
  email?: string
  sito?: string
  totale_posti_ordinaria?: string
  posti_accreditati_ordinaria?: string
  posti_disponibili_ordinaria?: string
  posti_occupati_ordinaria?: string
  lista_attesa_n_ordinaria?: string
  retta_min_ordinaria?: string
  retta_max_ordinaria?: string
  totale_posti_alzheimer?: string
  posti_accreditati_alzheimer?: string
  posti_disponibili_alzheimer?: string
  posti_occupati_alzheimer?: string
  lista_attesa_n_alzheimer?: string
  retta_min_alzheimer?: string
  retta_max_alzheimer?: string
}

export interface Rsa {
  id: string
  nome: string
  localita: string
  indirizzo: string
  provincia: string
  nucleoAlzheimer: boolean
  rettaMin: string
  rettaMax: string
  rettaMinAlzheimer: string
  rettaMaxAlzheimer: string
  listaAttesaOrdinaria: number
  listaAttesaAlzheimer: number
  dataAggiornamento: string
  info: string
  ambitoId: AmbitoId
  // campi dettaglio da scraping
  cudes: string
  telefono: string
  email: string
  sito: string
  totalePostiOrdinaria: number | null
  postiAccreditatiOrdinaria: number | null
  postiDisponibiliOrdinaria: number | null
  postiOccupatiOrdinaria: number | null
  rettaMinNum: number | null    // valore numerico parsed
  rettaMaxNum: number | null
  rettaMinAlzheimerNum: number | null
  rettaMaxAlzheimerNum: number | null
  totalePostiAlzheimer: number | null
  postiAccreditatiAlzheimer: number | null
  postiDisponibiliAlzheimer: number | null
  postiOccupatiAlzheimer: number | null
}

function resolveAmbitoId(localita: string): AmbitoId {
  const loc = localita.trim().toUpperCase()
  const MONZA = ['MONZA', 'BRUGHERIO', 'VILLASANTA', 'MUGGIO', 'MUGGIÒ']
  const CARATE = ['CARATE', 'BESANA', 'GIUSSANO', 'LISSONE', 'MACHERIO', 'BIASSONO', 'TRIUGGIO', 'VERANO', 'SOVICO']
  const DESIO = ['DESIO', 'BOVISIO', 'CESANO', 'LIMBIATE', 'LENTATE', 'MEDA', 'NOVA MILANESE', 'SEVESO', 'VAREDO']
  const SEREGNO = ['SEREGNO', 'BARLASSINA', 'CERIANO', 'COGLIATE', 'MISINTO', 'LAZZATE', 'MEDA']
  const VIMERCATE = ['VIMERCATE', 'AGRATE', 'ARCORE', 'BERNAREGGIO', 'BURAGO', 'CAPONAGO', 'CARNATE', 'CONCOREZZO', 'CORNATE', 'LESMO', 'ORNAGO', 'RONCO', 'SULBIATE', 'USMATE']
  if (MONZA.some(n => loc.includes(n))) return 'monza'
  if (CARATE.some(n => loc.includes(n))) return 'carate'
  if (DESIO.some(n => loc.includes(n))) return 'desio'
  if (SEREGNO.some(n => loc.includes(n))) return 'seregno'
  if (VIMERCATE.some(n => loc.includes(n))) return 'vimercate'
  return 'monza'
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

function parseEuro(s: string | undefined): number | null {
  if (!s) return null
  const n = parseFloat(s.replace(/[€\s]/g, '').replace(',', '.'))
  return isNaN(n) ? null : n
}

function parseNum(s: string | undefined): number | null {
  if (!s) return null
  const n = parseInt(s)
  return isNaN(n) ? null : n
}

export const RSA_LIST: Rsa[] = (rsaRaw as RawRsa[]).map((entry, index) => ({
  id: `${slugify(entry['Nome struttura'])}-${index}`,
  nome: entry['Nome struttura'],
  localita: entry['Località'],
  indirizzo: entry['Indirizzo'],
  provincia: entry['Provincia'],
  nucleoAlzheimer: entry['Nucleo Alzheimer'].trim().toUpperCase() === 'SI',
  rettaMin: entry['retta_min_ordinaria'] || entry['Retta Min.'],
  rettaMax: entry['retta_max_ordinaria'] || entry['Retta Max.'],
  rettaMinAlzheimer: entry['retta_min_alzheimer'] || entry['Retta Min. Alzheimer'],
  rettaMaxAlzheimer: entry['retta_max_alzheimer'] || entry['Retta Max. Alzheimer'],
  listaAttesaOrdinaria: entry['Lista attesa ordinaria'] ?? 0,
  listaAttesaAlzheimer: entry['Lista attesa Alzheimer'] ?? 0,
  dataAggiornamento: entry['Data aggiornamento'],
  info: entry['Info'],
  ambitoId: resolveAmbitoId(entry['Località']),
  cudes: entry.cudes ?? '',
  telefono: entry.telefono ?? '',
  email: entry.email ?? '',
  sito: entry.sito ?? '',
  totalePostiOrdinaria: parseNum(entry.totale_posti_ordinaria),
  postiAccreditatiOrdinaria: parseNum(entry.posti_accreditati_ordinaria),
  postiDisponibiliOrdinaria: parseNum(entry.posti_disponibili_ordinaria),
  postiOccupatiOrdinaria: parseNum(entry.posti_occupati_ordinaria),
  rettaMinNum: parseEuro(entry.retta_min_ordinaria || entry['Retta Min.']),
  rettaMaxNum: parseEuro(entry.retta_max_ordinaria || entry['Retta Max.']),
  rettaMinAlzheimerNum: parseEuro(entry.retta_min_alzheimer || entry['Retta Min. Alzheimer']),
  rettaMaxAlzheimerNum: parseEuro(entry.retta_max_alzheimer || entry['Retta Max. Alzheimer']),
  totalePostiAlzheimer: parseNum(entry.totale_posti_alzheimer),
  postiAccreditatiAlzheimer: parseNum(entry.posti_accreditati_alzheimer),
  postiDisponibiliAlzheimer: parseNum(entry.posti_disponibili_alzheimer),
  postiOccupatiAlzheimer: parseNum(entry.posti_occupati_alzheimer),
}))
